import { FC, ReactElement, memo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { CloudflareAccountsQueryKeys, useDeleteCloudflareAccountsMutation, useGetByIdCloudflareAccountsQuery } from '@/modules/DomainsApi';

import { CloudflareAccountsOverviewView } from './CloudflareAccountsOverviewView';

interface Props {
  cloudflareAccountId: string;
}

export const CloudflareAccountsOverviewContainer: FC<Props> = memo(({ cloudflareAccountId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: cloudflareAccountData,
    error: cloudflareAccountError,
    isLoading: isCloudflareAccountLoading,
    isFetching: isCloudflareAccountFetching,
    isError: isCloudflareAccountError
  } = useGetByIdCloudflareAccountsQuery(cloudflareAccountId)({
    enabled: !!cloudflareAccountId
  });

  const { isLoading: isDeleteCloudflareAccountLoading, mutate: deleteCloudflareAccountMutation } =
    useDeleteCloudflareAccountsMutation(cloudflareAccountId)({
      onSuccess: async () => {
        appMessageApi.success('Cloudflare акаунт успішно видалено!');

        await queryClient.refetchQueries([CloudflareAccountsQueryKeys.GetAll]);

        navigate(AppRoutes.DomainsApiCloudflareAccounts);
      },
      onError: (error) => {
        messageOnError(error, appMessageApi);
      }
    });

  const handleDeleteCloudflareAccount = useCallback((): void => {
    deleteCloudflareAccountMutation();
  }, []);

  const handleEditCloudflareAccount = useCallback((): void => {
    navigate(AppRoutes.DomainsApiCloudflareAccountsEdit.replace(':cloudflareAccountId', cloudflareAccountId));
  }, [cloudflareAccountId]);

  return (
    <FallbackedLayout
      isLoading={ isCloudflareAccountLoading }
      isError={ isCloudflareAccountError }
      error={ cloudflareAccountError }
      data={ cloudflareAccountData }
    >
      { (cloudflareAccount): ReactElement => (
        <CloudflareAccountsOverviewView
          cloudflareAccount={ cloudflareAccount }
          isDeleteCloudflareAccountLoading={ isDeleteCloudflareAccountLoading }
          isDeleteCloudflareAccountDisabled={ isDeleteCloudflareAccountLoading || isCloudflareAccountFetching }
          handleDeleteCloudflareAccount={ handleDeleteCloudflareAccount }
          handleEditCloudflareAccount={ handleEditCloudflareAccount }
        />
      ) }
    </FallbackedLayout>
  );
});
